import { call, put, select, take } from 'redux-saga/effects';

import * as Actions from '../../../actions';
import * as Constants from '../../../constants';
import { getInDomain, noopWithErrorLog } from '../../../domains/externalUtils';
import { getTrackById } from '../../../lib/EntityUtils';
import * as Errors from '../../../lib/error';
import { cond, pipe } from '../../../sagas/Helpers';
import { putAndWait } from '../../../sagas/TaskManager';
import Logger from '../../../tools/Logger';
import * as Utils from '../../../utils';

const log = Logger.child({ component: 'mediator/useCases/replayTrack' });

export function* logEventForReplayTrack({ trackToken, eventSource }) {
  const logReplayTrackEventActionString = 'analytics.actions.logReplayTrackEvent';
  yield put(
    getInDomain(logReplayTrackEventActionString, noopWithErrorLog(logReplayTrackEventActionString))(
      trackToken,
      eventSource,
    ),
  );
}

export function* closeSessionHistoryIfOpen() {
  const isHistoryOpen = yield select(Utils.UIState.getIsSessionHistoryOpen);
  if (isHistoryOpen) {
    yield put(Actions.NowPlaying.closeSessionHistory({ isLeavingNowPlaying: false }));
  }
}

export function* addStationReplayTrackToPlaylist({ stationId, track, playlistIndex }) {
  yield call(
    putAndWait,
    getInDomain('playback.actions.addReplayTrackToPlaylist')(stationId, track, playlistIndex + 1),
  );
}

export function* logReplayTrackDrm() {
  const nowPlayingTrack = yield select(
    getInDomain('playback.utils.getCurrentAudioTrackFromActivePlaylist'),
  );
  yield put(Actions.Playback.logSkipDRM(nowPlayingTrack, Constants.Playback.DRMSkipReasons.REPLAY));
}

export function* logStationReplayTrackAnalytics() {
  const logTrackEndStationActionString = 'analytics.actions.logTrackEndStation';
  yield put(
    getInDomain(
      logTrackEndStationActionString,
      noopWithErrorLog(logTrackEndStationActionString),
    )('replay'),
  );
}

export function* playStationReplayTrack({ stationId }) {
  yield call(
    putAndWait,
    getInDomain('playback.actions.playTrackFromStation')({
      id: stationId,
      doRegisterSkipTrack: false,
      nextTrackForceNewPlaylist: false,
      next: true,
    }),
  );
}

export function* playAPSReplayTrack(params) {
  params.itemPandoraId = params.trackToken;
  yield call(putAndWait, getInDomain('playback.actions.replayItem')(params));
}

export function* registerStationReplayTrack() {
  yield put(Actions.Playback.registerReplayTrack());
}

export function* startReplayRewardsSync({ action, track, stationId }) {
  const syncReplayRewardsAction = action;
  syncReplayRewardsAction.payload.stationId = stationId;
  syncReplayRewardsAction.pandoraId = track.get('pandoraId');

  // TODO-PAYLOAD: WEBCA-2515
  if (!syncReplayRewardsAction.data) {
    syncReplayRewardsAction.data = syncReplayRewardsAction.payload;
  }

  // Decrement (use) re(use)if track required rewards. Throw exception if not enough rewards
  yield put(getInDomain('rewards.actions.syncReplayRewards')(syncReplayRewardsAction));
  yield take(Constants.Ads.Rewards.ActionTypes.SYNC_REPLAY_REWARDS_HANDLED);
}

export function* initiateRewardReplay() {
  yield put(
    getInDomain('rewards.actions.initiateRewardsThroughAPS')(
      Constants.Ads.Rewards.RewardTypes.REPLAYS,
    ),
  );
  yield take(Constants.Api.ApiActions.CREATE_REWARD_SUCCESS);
}

/**
 * This function will pipe all handlers to replay an APS track
 *
 * @param {string} sourceId - pandoraId of track's source
 * @param {string} trackToken - token for the audio track
 * @param {string} eventSource - how the replay was triggered
 */
export function* replayAPSTrack(sourceId, trackToken, eventSource) {
  const offerReplayReward = yield select(
    getInDomain('playback.selectors.shouldOfferReplayReward', false),
  );
  try {
    yield call(
      pipe([
        cond([[() => !!offerReplayReward, initiateRewardReplay]]),
        playAPSReplayTrack,
        logEventForReplayTrack,
        closeSessionHistoryIfOpen,
      ]),
      {
        sourceId,
        trackToken,
        eventSource,
      },
    );
  } catch (error) {
    log.error({ obj: error }, 'replayAPSTrack(): unhandled Error');
  }
}

/**
 * This function will pipe all handlers to replay a station track
 *
 * @param {string} stationId - stationId
 * @param {string} trackToken - trackToken
 * @param {string} eventSource - how the replay was triggered
 * @param {object} action - action
 * @returns {*}
 */
export function* replayStationTrack(stationId, trackToken, eventSource, action) {
  log.info('replayStationTrack: starting station replay track');
  try {
    const track = yield select(getTrackById, trackToken);
    const isUserEligibleForReplays = yield select(Utils.Listener.canUserReplay);
    const isTrackReplayable = Utils.Playlist.doesTrackHaveReplayRights(track);

    // Check if track is replayable or has the sufficient rights
    if (!isTrackReplayable || !isUserEligibleForReplays) {
      log.info('replayTrack(): track not replayable');
      yield put(
        Actions.Modal.openModalCoachmark(
          Constants.Playback.ExternalMessages.replayNotAvailableLicensing,
        ),
      );
      return;
    }

    // Check if playlistIndex and track exist or not, throw an error if they do not
    const playlistIndex = yield select(
      getInDomain('playback.utils.getPlaylistCurrentIndex'),
      stationId,
    );
    if (!track || playlistIndex === -1) {
      throw new Errors.ApplicationError(Constants.Playback.PlaybackErrors.GENERAL_PLAYBACK_ERROR);
    }

    // If user is addSupported start rewards sync, else replay station track
    const shouldListenerGetAds = yield select(Utils.Listener.shouldCurrentListenerGetAds);
    yield call(
      pipe([
        cond([[() => shouldListenerGetAds === true, pipe([startReplayRewardsSync])]]),
        addStationReplayTrackToPlaylist,
        logReplayTrackDrm,
        logStationReplayTrackAnalytics,
        playStationReplayTrack,
        registerStationReplayTrack,
        closeSessionHistoryIfOpen,
        logEventForReplayTrack,
      ]),
      {
        action,
        eventSource,
        playlistIndex,
        stationId,
        track,
        trackToken,
      },
    );
  } catch (error) {
    if (!(error instanceof Errors.TaskManagerError)) {
      log.error(
        { obj: error },
        'replayStationTrack(): unhandled Error. Error is not an instance of TaskManagerError',
      );
    }
    const rewardsError = error.getFirstErrorOfType(Errors.RewardsError);
    const coachmarkError = error.getFirstErrorOfType(Errors.CoachmarkError);
    if (rewardsError || coachmarkError) {
      const replayNotAvailableMessage = getInDomain(
        'playback.constants.PlaybackMessages.replayNotAvailable',
      );
      return yield put(Actions.Modal.openModalCoachmark(replayNotAvailableMessage));
    }
    log.error({ obj: error }, 'replayStationTrack(): unhandled Error');
  }

  return true;
}
